import { useMemo } from 'react';
import type { FC, VFC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { isSvpCategory } from '../../../../modules/helpers';
import {
  getCategoryPromoUrl,
  getCategoryVerticalImage,
} from '../../../../shared/helpers/categoryHelpers';
import {
  getAssetDisplayTitle,
  getAssetPromoUrl,
  getAssetVerticalImage,
  isCurrentlyLive,
  isUpcomingLive,
} from '../../../../shared/helpers/assetHelpers';
import { Splash } from '../../../Splash/Splash/Splash';
import { Badge } from '../../../Asset/shared/Badge/Badge';
import { UpcomingLiveBadge } from '../../../Asset/shared/UpcomingLiveBadge/UpcomingLiveBadge';
import { AssetButtons } from '../../../Splash/SplashButtons/AssetButtons';
import { useSplashTracking } from '../../../PageComponents/common/useSplashTracking';
import { useServerTime } from '../../../utils/useServerTime';
import type { Vendor } from '../../../../types';
import { CategoryButtons } from '../../../Splash/SplashButtons/CategoryButtons';
import { useVendor } from '../../../VendorPovider/VendorPovider';
import { useEpisodeData } from '../../../Splash/EpisodeButton/useEpisodeData';
import { getImageSources } from '../../../../helpers/images';
import type { ImageSources } from '../../../../helpers/images';
import { getAssetLabels } from '../../../Splash/AssetSplash/getAssetLabels';
import { useAppSelector } from '../../../../modules/hooks';
import { selectCategoryById } from '../../../../modules/categories';
import { getCategoryLabels } from '../../../Splash/CategorySplash/getCategoryLabels';

import * as styles from './MobileSplash.module.scss';

interface MobileSplashProps {
  item: SvpAssetAdditional | SvpCategoryAdditional;
  url: string;
  className?: string;
}

const getPreviewUrl = (item: SvpAssetAdditional | SvpCategoryAdditional) => {
  return isSvpCategory(item)
    ? getCategoryPromoUrl(item)
    : getAssetPromoUrl(item);
};

type CarouselCategorySplashProps = {
  item: SvpCategoryAdditional;
  previewUrl?: string;
  vendor: Vendor;
  lazy: boolean;
  hidden: boolean;
  onPreviewEnded?: () => void;
  verticalImageSources: ImageSources;
  className?: string;
  url: string;
};

const CarouselCategorySplash: VFC<CarouselCategorySplashProps> = ({
  item,
  previewUrl,
  vendor,
  lazy,
  hidden,
  onPreviewEnded,
  verticalImageSources,
  url,
}) => {
  const { trackButtonSplashClick } = useSplashTracking({
    elementType: 'MainCarousel',
  });

  const parentCategory = useAppSelector((state) =>
    selectCategoryById(state, vendor, item.parentId!)
  );

  const labels = useMemo(() => {
    return getCategoryLabels(item, parentCategory);
  }, [item, parentCategory]);

  const { label: subtitle } = useEpisodeData({ category: item, vendor });

  return (
    <Splash
      className={styles.splash}
      title={item.title}
      imageUrl={getCategoryVerticalImage(item)}
      previewUrl={previewUrl}
      description={item.additional.description || ''}
      lazy={lazy}
      hidden={hidden}
      onPreviewEnded={onPreviewEnded}
      subtitle={subtitle}
      labels={labels}
      variant="carousel"
      verticalImageSources={verticalImageSources}
      buttons={
        <CategoryButtons
          trackSplashClick={trackButtonSplashClick}
          category={item}
        />
      }
      redirectImageUrl={url}
    />
  );
};

export const MobileSplash: FC<MobileSplashProps> = ({
  item,
  url,
  className,
}) => {
  const lazy = false;
  const hidden = false;
  const previewUrl = getPreviewUrl(item);
  const { serverTime } = useServerTime();
  const { t } = useTranslation();
  const { vendor } = useVendor();

  const { trackButtonSplashClick } = useSplashTracking({
    elementType: 'MainCarousel',
  });

  let verticalImage: string | undefined;

  if (isSvpCategory(item)) {
    verticalImage = getCategoryVerticalImage(item);
  } else {
    verticalImage = getAssetVerticalImage(item);
  }

  const verticalImageSources = useMemo(
    () =>
      getImageSources({
        imageUrl: verticalImage,
        aspectRatio: 'vertical',
        imagePreset: 'assetsSliderVertical',
      }),
    [verticalImage]
  );

  if (isSvpCategory(item)) {
    return (
      <CarouselCategorySplash
        key={`category:${item.id}`}
        className={className}
        hidden={hidden}
        previewUrl={previewUrl}
        item={item}
        lazy={lazy}
        vendor={vendor}
        verticalImageSources={verticalImageSources}
        url={url}
      />
    );
  }

  const displayBadge =
    isCurrentlyLive(item, serverTime) || isUpcomingLive(item, serverTime);

  return (
    <Splash
      key={`asset:${item.id}`}
      className={cn(styles.splash, className)}
      title={getAssetDisplayTitle(item)}
      imageUrl={getAssetVerticalImage(item)}
      previewUrl={previewUrl}
      description={item.description}
      lazy={lazy}
      hidden={hidden}
      subtitle={item.category.title}
      variant="carousel"
      verticalImageSources={verticalImageSources}
      redirectImageUrl={url}
      labels={getAssetLabels(item)}
      badge={
        displayBadge ? (
          <>
            {isCurrentlyLive(item, serverTime) && (
              <Badge kind="live" label={t('Live')} />
            )}
            {isUpcomingLive(item, serverTime) && (
              <UpcomingLiveBadge asset={item} />
            )}
          </>
        ) : undefined
      }
      buttons={
        <AssetButtons trackSplashClick={trackButtonSplashClick} asset={item} />
      }
    />
  );
};
